import { Component, Inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { Select, Store } from '@ngxs/store';

import { EventService, SeoService, ToastService, ValidationService } from '../../core/services';
import { ConfirmComponent } from '../../shared/components';
import { GiftShareValidators, NAME_REGEXP } from '../../shared';
import { AuthActions, AuthState, StaticDataActions } from '../../state';
import { UserProfile } from '../../core/models';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { eventImage, userImage } from '../../core/helpers';
import { environment } from '../../../environments/environment';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'gs-event-detail',
  templateUrl: './event-detail.component.html',
  standalone: false
})
export class EventDetailComponent implements OnInit, OnDestroy {
  @Select(AuthState.isCorporateUser) isCorporateUser$: Observable<boolean>;

  reportCauses: any[] = [];

  form: FormGroup = this.formBuilder.group({
    name: [null, [Validators.required, Validators.maxLength(100), Validators.pattern(NAME_REGEXP)]],
    email: [null, [Validators.required, Validators.maxLength(256), GiftShareValidators.email]],
    causeId: [null, [Validators.required]],
    reason: [null, [Validators.maxLength(1000)]],
  });

  formCoupon: FormGroup = this.formBuilder.group({
    message: [null, [Validators.maxLength(1000)]],
  });

  user: UserProfile;
  event: any;
  eventCode: string = '';

  hasCommentsEnabled: boolean = false;
  hasContributionsEnabled: boolean;

  private reportModal: NgbModalRef;
  private giftCardModal: NgbModalRef;
  private subscriptions: Subscription[] = [];

  get isEventOwner(): boolean {
    return this.event?.userId == this.user?.id;
  }

  get eventUrl(): string {
    return environment.frontEndUrl + '/eveniment/' + this.eventCode;
  }

  constructor(
    private eventService: EventService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private ngxUiLoaderService: NgxUiLoaderService,
    private formBuilder: FormBuilder,
    public vs: ValidationService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private seoService: SeoService,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  ngOnInit() {
    this.store.dispatch(new StaticDataActions.GetReportCauses());

    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        }),

      this.store.select<any>(state => state.staticData.publicSettings)
        .pipe(filter(x => !!x))
        .subscribe((data: any) => {
          this.hasCommentsEnabled = data.comments;
          this.hasContributionsEnabled = data.contribution;
        }),

      this.store.select<any>(state => state.staticData.reportCauses)
        .pipe(filter(x => !!x))
        .subscribe((data: any) => {
          const defaultOptions = [{
            id: null,
            name: 'Selecteaza motivul'
          }];

          this.reportCauses = defaultOptions.concat(data);
        }),

      this.route.paramMap.subscribe(x => {
        this.eventCode = x.get('eventCode');
        this.getEvent();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Event Pause/Continue Functionality.
   * @param {any} eventCode
   * @memberof CampaignDetailComponent
   */
  onPauseClick(eventCode) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = `Confirma ${this.event.isStarted ? 'oprirea' : 'repornirea'}`;
    modalRef.componentInstance.description = `Esti sigur ca doresti sa ${this.event.isStarted ? 'opresti temporar' : 'repornesti'} acest eveniment?`;
    modalRef.componentInstance.acceptLabel = this.event.isStarted ? 'Opreste' : 'Reporneste';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done
        .subscribe(result => {
          if (result === 'accept') {
            this.ngxUiLoaderService.start();
            this.subscriptions.push(
              this.eventService.pauseEvent(eventCode)
                .pipe(
                  catchError(e => {
                    this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
                    return throwError(e);
                  }),
                  finalize(() => {
                    this.ngxUiLoaderService.stop();
                  })
                )
                .subscribe(() => {
                  this.event.isStarted = !this.event.isStarted;
                  this.toastService.show('Succes', this.event.isStarted ? 'Evenimentul a fost repornit.' : 'Evenimentul a fost oprit temporar.', 'success');
                })
            );

          }
        })
    );
  }

  /**
   * Stop event triggered by user
   *
   */
  onStop(eventCode: string) {
    let message: string;
    if (!this.event?.recipientName) {
      message = 'Evenimentul tau va fi inchis definitiv. Esti sigur ca doresti inchiderea evenimentului?';
    }
    else if (this.event.totalContributionAmount < 1) {
      message = 'Evenimentul tau va fi inchis definitiv. Cardul GiftShare nu va fi generat pentru ca nu exista contributii la acest eveniment.';
    }
    else {
      message = `Evenimentul tau va fi inchis definitiv. Va fi generat un card GiftShare cu valoarea ${this.event.totalContributionAmount} lei.`;
    }

    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Inchide evenimentul';
    modalRef.componentInstance.description = message;
    modalRef.componentInstance.acceptLabel = 'Inchide';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done
        .subscribe(result => {
          if (result === 'accept') {
            this.ngxUiLoaderService.start();
            this.subscriptions.push(
              this.eventService.stopEvent(eventCode)
                .pipe(
                  catchError(e => {
                    this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
                    return throwError(e);
                  }),
                  finalize(() => {
                    this.ngxUiLoaderService.stop();
                  })
                )
                .subscribe(() => {
                  this.store.dispatch(new AuthActions.GetCurrentUser());
                  this.toastService.show('Succes', 'Evenimentul a fost oprit.', 'success');
                  this.refresh();
                })
            );
          }
        })
    );
  }

  onDownloadPdfSubmit() {
    if (!this.formCoupon.valid) {
      this.vs.triggerValidation(this.formCoupon);
      return;
    }

    const model = {
      ...this.formCoupon.value
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.eventService.generatePdf(this.eventCode, model, `card-cadou-giftshare-${this.eventCode}.pdf`)
        .pipe(
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.formCoupon.reset();
          this.giftCardModal.close();
        })
    );
  }

  getEvent() {
    this.subscriptions.push(
      this.eventService.getPublicEventDetailsByCode(this.eventCode)
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((event: any) => {
          if (event.status == 'Deleted') {
            this.router.navigate(['/error']);
            return;
          }

          this.event = event;
          this.event.image = eventImage(event);
          this.event.userImage = userImage(event.userImage);
          this.event.recipientImage = userImage(event.recipientImage);
          this.event.targetProgress =
            event.targetProgress == null
              ? 0
              : event.targetProgress < 100 ? event.targetProgress : 100;

          event.updates.forEach((x) => {
            x.userImage = userImage(x.userImage);
          });

          event.comments.forEach((x) => {
            x.userImage = userImage(x.userImage);

            x.replies.forEach((y) => {
              y.userImage = userImage(y.userImage);
            });
          });

          if (this.event.isSuspended && !this.isEventOwner) {
            this.router.navigate(['/eveniment-suspendat']);
            return;
          }

          // Custom SEO tags
          this.seoService.setTitle(this.event.name);
          this.seoService.setDescription(this.event.description);
          this.seoService.setImage(this.event.image);
        })
    );
  }

  refresh(): void {
    this.document.location.reload();
  }

  onOpenReportModalClick(template: TemplateRef<any>) {
    if (this.user) {
      this.form.patchValue({
        name: this.user.name,
        email: this.user.email,
      });

      if (this.user.name && this.user.email) {
        this.form.controls.name.disable();
        this.form.controls.email.disable();
      }
    }

    this.reportModal = this.modalService.open(template);
  }

  onReportSubmit() {
    if (!this.form.valid) {
      this.vs.triggerValidation(this.form);
      return;
    }

    const model = {
      ...this.form.getRawValue()
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.eventService.reportEvent(this.eventCode, model)
        .pipe(
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.toastService.show('Succes', 'Evenimentul a fost raportat cu succes. Vom analiza informatiile in cel mai scurt timp posibil.', 'success');
          this.reportModal.close();
        })
    );
  }

  onOpenGiftCardModalClick(template: TemplateRef<any>) {
    this.giftCardModal = this.modalService.open(template);
  }

}
